import React from 'react';
import { Card } from '@frontend/shadcn/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { ChartBarIcon, ChartLineUpIcon, ChartLineDownIcon } from '@revfluence/fresh-icons/regular/esm';
import { Empty } from '@frontend/app/refresh-components';
import { GraphCardHeader, GraphCardTitle, GraphCardContent } from '../../../components/graph-card';
import { DateType } from '../../types';
import { dateTypeLabels } from '../date-range-form/date-range-form.types';
import type { TimeSeriesDataPoint } from '../../types/time-series.types';
import { formatShortNumber } from '../../utils/format';

interface TrendCardProps {
  title: string;
  hasData?: boolean;
  dateType?: DateType;
  timeSeriesData?: TimeSeriesDataPoint[];
  colors: Record<string, string>;
  emptyStateTitle: string;
  emptyStateDescription: string;
}

export const TrendCard: React.FC<TrendCardProps> = ({
  title,
  hasData = true,
  dateType = 'last6Months',
  timeSeriesData = [],
  colors,
  emptyStateTitle,
  emptyStateDescription,
}) => {
  const chartData = React.useMemo(() => {
    if (timeSeriesData && timeSeriesData.length > 0) {
      return timeSeriesData;
    }
    return [];
  }, [timeSeriesData]);

  const total = React.useMemo(() => {
    if (!chartData.length) return 0;
    return chartData.reduce((total, dataPoint) => {
      return total + Object.keys(colors).reduce((sum, platform) => {
        const value = dataPoint[platform as keyof TimeSeriesDataPoint];
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);
    }, 0);
  }, [chartData, colors]);

  const trendData = React.useMemo(() => {
    if (chartData.length < 2) {
      return { percentage: 0, isPositive: true, hasData: false };
    }

    const firstPoint = chartData[0];
    const lastPoint = chartData[chartData.length - 1];

    const firstTotal = Object.keys(colors).reduce((sum, platform) => {
      const value = firstPoint[platform as keyof TimeSeriesDataPoint];
      return sum + (typeof value === 'number' ? value : 0);
    }, 0);

    const lastTotal = Object.keys(colors).reduce((sum, platform) => {
      const value = lastPoint[platform as keyof TimeSeriesDataPoint];
      return sum + (typeof value === 'number' ? value : 0);
    }, 0);

    const percentageChange = firstTotal > 0 ? ((lastTotal - firstTotal) / firstTotal) * 100 : 0;

    return {
      percentage: Math.abs(percentageChange),
      isPositive: percentageChange >= 0,
      hasData: true
    };
  }, [chartData, colors]);

  const TrendTooltipContent = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    const monthTotal = payload.reduce((sum, entry) => {
      return sum + (typeof entry.value === 'number' ? entry.value : 0);
    }, 0);

    return (
      <div
        className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[160px]"
        role="tooltip"
        aria-label={`${title} data for ${label}`}
      >
        <div className="text-sm font-semibold text-gray-900 mb-2">
          {label} {title}
        </div>
        <div className="space-y-1">
          {payload.map((entry) => {
            const platform = entry.dataKey as string;
            const value = entry.value as number;
            const color = colors[platform as keyof typeof colors];

            return (
              <div key={platform} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                  />
                  <span className="text-xs text-gray-600">{platform}</span>
                </div>
                <span className="text-xs font-medium text-gray-900">
                  {formatShortNumber(value)}
                </span>
              </div>
            );
          })}
        </div>
        <div className="border-t border-gray-100 mt-2 pt-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700">Total</span>
            <span className="text-xs font-bold text-gray-900">
              {formatShortNumber(monthTotal)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const shouldShowData = hasData !== false && chartData.length > 0;

  return (
    <Card className="h-full" role="region" aria-label={`${title} trends by platform`}>
      <GraphCardHeader className="border-b">
        <GraphCardTitle>{title}</GraphCardTitle>
      </GraphCardHeader>
      <GraphCardContent className="pt-4 flex flex-col justify-between flex-1">
        {!shouldShowData ? (
          <div
            className="h-[280px] flex items-center justify-center overflow-hidden"
            role="graphics-document"
            aria-label={`No ${title.toLowerCase()} data available`}
          >
            <Empty
              icon={ChartBarIcon}
              title={emptyStateTitle}
              description={emptyStateDescription}
              size="lg"
            />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-foreground">
                {formatShortNumber(total)}
              </div>
              <div className="flex items-center gap-2">
                {trendData.hasData && (
                  <span className={`text-xs font-medium flex items-center px-1 rounded border ${
                    trendData.isPositive
                      ? 'text-green-600 border-green-200 bg-green-50'
                      : 'text-red-600 border-red-200 bg-red-50'
                  }`}
                  >
                    {trendData.isPositive ? (
                      <ChartLineUpIcon className="w-3 h-3 mr-1" aria-hidden="true" />
                    ) : (
                      <ChartLineDownIcon className="w-3 h-3 mr-1" aria-hidden="true" />
                    )}
                    {trendData.percentage.toFixed(1)}%
                  </span>
                )}
                <span className="text-sm text-[#5D6974]">
                  Total {title.toLowerCase()} in {dateTypeLabels[dateType].toLowerCase()}
                </span>
              </div>
            </div>

            <div className="h-32 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={formatShortNumber} />
                  {Object.entries(colors).map(([platform, color]) => (
                    <Bar
                      key={platform}
                      dataKey={platform}
                      stackId={title.toLowerCase()}
                      fill={color}
                      radius={platform === 'Instagram' ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                  <Tooltip
                    content={TrendTooltipContent}
                    cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="flex flex-wrap justify-center gap-4 text-xs mt-4">
              {Object.entries(colors).map(([platform, color]) => (
                <div key={platform} className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
                  <span className="text-gray-600">{platform}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </GraphCardContent>
    </Card>
  );
};
